import React from 'react';
import './AnalysisBarChart.css';

/**
 * Gepaarte horizontale Balken IST + SOLL pro Zeile.
 * Skala bezieht sich auf max(IST, SOLL) ueber alle Zeilen.
 *
 * @param {{
 *   rows: Array<{ key: string, label: string, ist: number, soll: number, color?: string, count?: number }>,
 *   formatIst: (n: number) => string,
 *   formatSoll: (n: number) => string,
 *   darkMode?: boolean,
 *   onRowClick?: (key: string) => void,
 *   maxRows?: number,
 *   showSoll?: boolean,
 *   sortBy?: 'ist' | 'soll' | 'none',
 * }} props
 */
function AnalysisBarChart({
  rows,
  formatIst,
  formatSoll,
  darkMode = false,
  onRowClick,
  maxRows,
  showSoll = true,
  sortBy = 'ist',
}) {
  const safeRows = Array.isArray(rows) ? rows.slice() : [];
  if (sortBy === 'ist') {
    safeRows.sort((a, b) => (b.ist || 0) - (a.ist || 0));
  } else if (sortBy === 'soll') {
    safeRows.sort((a, b) => (b.soll || 0) - (a.soll || 0));
  }
  const visible = (typeof maxRows === 'number' && maxRows > 0) ? safeRows.slice(0, maxRows) : safeRows;
  const maxVal = visible.reduce((m, r) => Math.max(m, r.ist || 0, showSoll ? (r.soll || 0) : 0), 0);

  if (visible.length === 0 || maxVal <= 0) {
    return <div className={`analysis-bar-chart ${darkMode ? 'dark-mode' : ''}`}><div className="analysis-bar-empty">Keine Daten fuer eine Darstellung.</div></div>;
  }

  return (
    <div className={`analysis-bar-chart ${darkMode ? 'dark-mode' : ''}`}>
      {visible.map(r => {
        const ist = r.ist || 0;
        const soll = r.soll || 0;
        const pctIst = maxVal > 0 ? (ist / maxVal) * 100 : 0;
        const pctSoll = maxVal > 0 ? (soll / maxVal) * 100 : 0;
        const color = r.color || '#667eea';
        const clickable = !!onRowClick;
        return (
          <div
            key={r.key}
            className={`analysis-bar-row ${clickable ? 'clickable' : ''}`}
            onClick={clickable ? () => onRowClick(r.key) : undefined}
            title={clickable ? `Klicken fuer Details${r.count != null ? `: ${r.count} Messpunkt(e)` : ''}` : ''}
          >
            <div className="analysis-bar-row-header">
              <span className="analysis-bar-row-label" title={r.label}>{r.label}</span>
              <span className="analysis-bar-row-values">
                <span className="analysis-bar-row-value-ist" title="IST">{formatIst(ist)}</span>
                {showSoll && (
                  <>
                    <span className="analysis-bar-row-sep">/</span>
                    <span className="analysis-bar-row-value-soll" title="SOLL">{formatSoll(soll)}</span>
                  </>
                )}
                {r.count != null && <span className="analysis-bar-row-count">({r.count})</span>}
              </span>
            </div>
            <div className="analysis-bar-row-tracks">
              <div className="analysis-bar-track-row">
                <span className="analysis-bar-tag analysis-bar-tag-ist">IST</span>
                <div className="analysis-bar-track">
                  <div
                    className="analysis-bar-fill analysis-bar-fill-ist"
                    style={{ width: pctIst + '%', backgroundColor: color, minWidth: ist > 0 ? '4px' : 0 }}
                  />
                </div>
              </div>
              {showSoll && (
                <div className="analysis-bar-track-row">
                  <span className="analysis-bar-tag analysis-bar-tag-soll">SOLL</span>
                  <div className="analysis-bar-track">
                    <div
                      className="analysis-bar-fill analysis-bar-fill-soll"
                      style={{ width: pctSoll + '%', borderColor: color, minWidth: soll > 0 ? '4px' : 0 }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
      {typeof maxRows === 'number' && safeRows.length > visible.length && (
        <div className="analysis-bar-more">+{safeRows.length - visible.length} weitere</div>
      )}
    </div>
  );
}

export default AnalysisBarChart;
